import moment from 'moment-timezone';
import { getFacility } from './facility';
import {
    queryGetPaymentApplicationMonths,
    queryGetFacilitySettings,
} from '../Queries';

const paymentApplicationMonthFields = [
    'month',
    'year',
    'status',
];

const facilitySettingsFields = ['future_month_application'];

/**
 * Returns an array of the facility's closed payment application months
 * @param   {string}    facilityId
 * @returns {Promise<[PaymentApplicationMonth]>}
 */
const getUnavailablePastMonths = async (facilityId) => {
    const response = await queryGetPaymentApplicationMonths({
        facility_id: facilityId,
    }, paymentApplicationMonthFields);

    if (response.errors) throw response.errors;

    const months = response.data.getPaymentApplicationMonths;

    const unavailablePastMonths = (months ?? []).reduce((unavailableMonths, month) => {
        if (month.status === 'CLOSED') unavailableMonths.push(month);
        return unavailableMonths;
    }, []);

    return unavailablePastMonths;
};

/**
 * Returns the facility's max allowed application date or null if there's no limit
 * @param   {string}    facilityId
 * @returns {Promise<Moment|null>}
 */
const getMaxApplicationDate = async (facilityId) => {
    const facility = await getFacility(facilityId);
    const facilityTimezone = facility.fields.timezone.value;

    const response = await queryGetFacilitySettings({
        facility_id: facilityId,
    }, facilitySettingsFields);

    if (response.errors) throw response.errors;

    const facilitySettings = response.data.getFacilitySettings;
    const futureMonthApplication = facilitySettings?.future_month_application ?? 'NOT_ALLOWED';

    const today = moment().tz(facilityTimezone);
    switch (futureMonthApplication) {
        case 'NOT_ALLOWED':
            return today;
        case 'ALLOWED_CURRENT_MONTH':
            return moment(today).endOf('month');
        case 'ALLOWED_NO_LIMIT':
            return null;
        default:
            throw new Error('Invalid Future Month Application Setting');
    }
};

/**
 * Returns an object with 2 properties:
 * 1. An array of the facility's unavailable past application months
 * 2. The facility's max allowed application date
 * @param   {string}    facilityId
 * @returns {Promise<object>}
 */
export const getApplicationDateConfig = async (facilityId) => {
    const unavailablePastMonths = await getUnavailablePastMonths(facilityId);
    const maxApplicationDate = await getMaxApplicationDate(facilityId);

    return {
        unavailablePastMonths,
        maxApplicationDate,
    };
};

/**
 * @param   {Moment}    date
 * @param   {object}    applicationDateConfig
 * @param   {string}    facilityTimezone
 * @returns {boolean}
 */
export const dateIsUnavailable = (date, applicationDateConfig, facilityTimezone) => {
    const {
        unavailablePastMonths,
        maxApplicationDate,
    } = applicationDateConfig;

    const now = moment().tz(facilityTimezone);
    const minApplicationDate = moment(now).subtract(1, 'year').startOf('month');

    if (maxApplicationDate) {
        if (date.isAfter(maxApplicationDate, 'day')) return true;
    }
    if (date.isBefore(minApplicationDate, 'day')) return true;

    const dateInClosedMonth = unavailablePastMonths.find(pastMonth => {
        const { month, year } = pastMonth;
        const monthMoment = moment(`${month} ${year}`, 'MMM YYYY').tz(facilityTimezone);

        return date.isSame(monthMoment, 'month');
    });

    return !!(dateInClosedMonth);
}
